<template>
  <div class="m-label" :class="labelClasses" @click="handleClick" v-bind="$attrs">
    <img
      v-if="swingIconUrl"
      :src="swingIconUrl"
      :alt="label || icon"
      class="m-label__icon m-label__icon-image"
      :style="{ width: iconSize + 'px', height: iconSize + 'px' }"
    />
    <i
      v-else-if="hasIcon"
      :class="iconClass"
      :style="{ fontSize: iconSize + 'px', color: iconColor }"
      class="m-label__icon"
    />

    <span v-if="label" :class="{ 'm-label__text--with-icon': hasIcon || swingIconUrl }" class="m-label__text">
      <span v-if="isHtmlLabel" v-html="htmlContent"></span>
      <span v-else>{{ label }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { sanitizePlatformDisplayHtml } from '@/common/utils/html-sanitizer'
import { resolvePlatformIcon } from '@/common/utils/resolvePlatformIcon'
import { computed } from 'vue'

interface Props {
  icon?: string
  label?: string
  isLink?: boolean
  iconSize?: string | number
  iconColor?: string
  align?: 'left' | 'center' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  icon: '',
  label: '',
  isLink: false,
  iconSize: 16,
  iconColor: 'inherit',
  align: 'left'
})

const emit = defineEmits(['click'])

// Проверяем, есть ли иконка для отображения
const hasIcon = computed(() => !!props.icon)

const iconResolution = computed(() => (props.icon ? resolvePlatformIcon(props.icon) : null))

const swingIconUrl = computed(() =>
  iconResolution.value?.kind === 'image' ? iconResolution.value.url : '',
)

// Класс для иконки (PrimeIcons, если нет Swing PNG)
const iconClass = computed(() => {
  if (!props.icon) return ''
  if (iconResolution.value?.kind === 'image') return ''
  return getPrimeIcon(props.icon)
})
// Check if label contains HTML
const isHtmlLabel = computed(() => {
  if (!props.label || typeof props.label !== 'string') return false
  const trimmed = props.label.trim()
  return trimmed.toLowerCase().startsWith('<html>') || trimmed.toLowerCase().startsWith('<html ')
})

const htmlContent = computed(() => {
  if (!isHtmlLabel.value || !props.label) return ''
  return sanitizePlatformDisplayHtml(props.label)
})

// Классы для корневого элемента
const labelClasses = computed(() => [
  'm-label',
  `m-label--align-${props.align}`,
  {
    'm-label--link': props.isLink,
    'm-label--with-icon': hasIcon.value || !!swingIconUrl.value,
    'm-label--text-only': !hasIcon.value && !swingIconUrl.value && props.label,
    'm-label--icon-only': (hasIcon.value || !!swingIconUrl.value) && !props.label,
  }
])

// Обработчик клика
const handleClick = (event: MouseEvent) => {
  if (props.isLink) {
    emit('click', event)
  }
}
</script>

<style scoped lang="scss">
.m-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  transition: all 0.2s ease;
  max-width: 100%;

  &--link {
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      transform: translateY(1px);
    }
  }

  &--with-icon {
    .m-label__text {
      padding-left: 4px;
    }
  }

  &--text-only {
    padding: 4px 0;
  }

  &--icon-only {
    .m-label__icon {
      margin: 0;
    }
  }
}

.m-label__icon {
  flex-shrink: 0;
  transition: inherit;
}

.m-label__icon-image {
  object-fit: contain;
}

.m-label__text {
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  line-height: 1.2;
  transition: inherit;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  hyphens: auto;
  max-width: 100%;
  flex: 1;
  min-width: 0;

  &--with-icon {
    padding-left: 4px;
  }

  // Styles for HTML content
  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 0;
    padding: 0;
    font-weight: inherit;
    line-height: inherit;
  }

  :deep(p) {
    margin: 0;
    padding: 0;
  }

  :deep(table) {
    margin: 0;
    padding: 0;
  }
}

// Адаптивные стили
@media (max-width: 768px) {
  .m-label {
    gap: 6px;
  }

  .m-label__icon {
    font-size: 18px !important;
  }

  .m-label__text {
    font-size: 0.9em;
  }
}

// Плотный вариант
.m-label--dense {
  gap: 4px;
  min-height: 20px;

  .m-label__icon {
    font-size: 16px !important;
  }

  .m-label__text {
    font-size: 0.85em;
  }
}

// Выравнивание
.m-label--align-left {
  justify-content: flex-start;
  text-align: left;
}

.m-label--align-center {
  justify-content: center;
  text-align: center;
}

.m-label--align-right {
  justify-content: flex-end;
  text-align: right;
  width: 100%;
  max-width: 100%;
  min-width: 0;

  /* Одна строка; при нехватке места — многоточие слева от обрезки (text-align: right) */
  .m-label__text {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: normal;
    overflow-wrap: normal;
    hyphens: manual;
  }
}

@media (max-width: 768px) {
  .m-label--align-right .m-label__text {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    flex: 1;
  }
}
</style>
